import { useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { Plan, ProfessionalCreditsBreakdown } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { convertCreditsToUSD, isFreePlan, isFreeTrial } from '@common/credits/ProfessionalEdition/utils';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { useUserTeams } from '@common/userHooks';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { CreditCardBIcon, IconSize, InfoCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { overlayStyle } from '@prophecy/ui-v3/Ellipsis';
import { Stack } from '@prophecy/ui-v3/Layout/Stack';
import { toast } from '@prophecy/ui-v3/Toast';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography/Text';
import { formatCurrency, roundToDecimals } from '@prophecy/utils/number';
import { useSearchParamState } from '@prophecy/utils/react/hooks';
import { pluralize } from '@prophecy/utils/string';
import { formatDistanceToNow } from 'date-fns';
import { useMixPanel } from 'frontend/core/src/context/mixpanel/context';
import { useUser } from 'frontend/core/src/context/user';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { BillingCard } from '../../components/BillingCard';
import { getCustomerPortal, getPaymentMethods } from '../data/api';
import { PaymentMethod, PaymentMethodType } from '../data/types';
import { computePlanRenewalInfo, formatPlanUnit } from '../utils';
import { ManageMonthlySpendingDialog } from './ManageMonthlySpendingDialog';

const formatCreditBreakdown = (
  breakdown: ProfessionalCreditsBreakdown,
  pricePerCredit: number,
  creditBalance: number
) => {
  const creditItems = [
    { label: 'AI Requests', value: breakdown.aiRequestsCredits },
    { label: 'Compute', value: breakdown.computeCredits },
    { label: 'Data Egressed', value: breakdown.dataEgressedCredits },
    { label: 'Data Stored', value: breakdown.dataStoredCredits },
    { label: 'Additional Users', value: breakdown.additionalUsersCredits }
  ];
  return (
    <Stack direction='vertical' gap={theme.spaces.x3}>
      {creditItems.map(({ label, value }) => (
        <Stack key={label} direction='horizontal' align='space-between' gap={theme.spaces.x16}>
          <Text level='sm' tone={theme.colors.gray600}>
            {label}
          </Text>
          <Text level='sm' weight={theme.fontWeight.medium} tone={theme.textColor.primary}>
            {formatCurrency(convertCreditsToUSD(value, pricePerCredit))}
          </Text>
        </Stack>
      ))}
      {creditBalance !== undefined && (
        <Stack direction='horizontal' align='space-between' gap={theme.spaces.x16}>
          <Text level='sm' tone={theme.colors.gray600}>
            Credit Balance
          </Text>
          <Text level='sm' weight={theme.fontWeight.medium} tone={theme.textColor.primary}>
            {formatCurrency(creditBalance)}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

const computeResetPeriod = (expirationDate: string) => {
  try {
    const date = new Date(expirationDate).getTime();
    const now = new Date().getTime();
    if (date <= now) {
      return 'Expired';
    }
    return `Resets ${formatDistanceToNow(date, { addSuffix: true })}`;
  } catch (error) {
    return 'Invalid date';
  }
};

export const BillingOverview = ({ plans, onPlanManage }: { plans: Plan[]; onPlanManage: () => void }) => {
  const { isLoading } = useUserTeams();

  const { meteringInfo, error, customerBalance } = useProfessionalEditionCreditsContext();
  const {
    creditsConsumed,
    totalCreditsAllocated,
    userCreditsConsumed,
    additionalUsers,
    pricePerCredit,
    usageLimit = {
      alertAmount: 0,
      budgetAmount: 0
    },
    autoUpgraded
  } = meteringInfo;

  // Same source as the credits shown in the user profile dropdown so the two can't drift
  const { serviceCreditsConsumed, serviceCreditsAllocated, serviceUsagePercentage } = useProfessionalCreditStatus();

  const { currentWorkspaceId, currentWorkspace } = useUserWorkspace();
  const [updateLimits] = useSearchParamState('update-limits', {
    initialValue: false,
    deserialize: (value) => value === 'true'
  });
  const [showManageMonthlySpendingDialog, setShowManageMonthlySpendingDialog] = useState(updateLimits);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(undefined);

  const { user } = useUser();

  const { track } = useMixPanel();

  const computeAdditionalUsageAmount = () => {
    const { creditsBreakdown, outstandingBalance, creditBalance = 0 } = customerBalance || {};
    const additionalUsageAmount = outstandingBalance
      ? outstandingBalance
      : Math.max(0, convertCreditsToUSD(creditsConsumed - totalCreditsAllocated, pricePerCredit));

    if (!creditsBreakdown || !outstandingBalance) {
      if (isFreePlan(activePlan?.name)) {
        return (
          <Text level='xl' weight={theme.fontWeight.semiBold} tone={theme.colors.gray300}>
            Not applicable
          </Text>
        );
      }
      return formatCurrency(roundToDecimals(additionalUsageAmount));
    }

    return (
      <Stack direction='horizontal' gap={theme.spaces.x4} alignY='start'>
        <Text level='lg' weight={theme.fontWeight.semiBold} tone={theme.textColor.primary}>
          {formatCurrency(roundToDecimals(additionalUsageAmount))}
        </Text>
        <Tooltip
          title={formatCreditBreakdown(creditsBreakdown, pricePerCredit, creditBalance)}
          overlayStyle={overlayStyle}>
          <Stack direction='horizontal' gap={theme.spaces.x4}>
            <StyledInfoCircleIcon type={'default'} size={IconSize.xxs} />
          </Stack>
        </Tooltip>
      </Stack>
    );
  };

  useEffect(() => {
    if (!user.id || !currentWorkspaceId) {
      return;
    }
    getPaymentMethods(user.id, currentWorkspaceId).then((response) => {
      const { data, success } = response;

      if (!success) {
        return;
      }
      const { type, details } = data;
      const paymentMethod = {
        type,
        details
      };
      setPaymentMethod(paymentMethod);
    });
  }, [user.id, currentWorkspaceId]);

  const handleManagePaymentMethod = async () => {
    try {
      if (!currentWorkspaceId) {
        throw new Error('Current workspace ID is not set');
      }
      const { data, success } = await getCustomerPortal(user.id, currentWorkspaceId);
      if (!success) {
        throw new Error('Failed to get customer portal');
      }

      // Track customer portal access
      track(EntityEvents.settings.billing.accessCustomerPortal, {
        teamId: currentWorkspaceId
      });
      // redirect to the stripe portal
      window.open(data.url, '_blank');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to manage payment method';
      toast.error({
        'data-test-id': 'manage-payment-method-error',
        content: errorMessage,
        closeable: true
      });
    }
  };

  const activePlan = plans.find((plan) => plan.name === meteringInfo.plan);

  const paymentMethodTooltip = useMemo(() => {
    if (meteringInfo.userID !== user.id) {
      return 'Please contact the team admin to manage the payment method';
    }
    return 'Payment method for this workspace is not set';
  }, [meteringInfo.userID, user.id]);

  const formatPaymentMethod = () => {
    if (!paymentMethod) {
      return (
        <Stack direction='horizontal' gap={theme.spaces.x4}>
          <Text level='xl' weight={theme.fontWeight.semiBold} tone={theme.colors.gray300}>
            Not available
          </Text>
          {paymentMethodTooltip && (
            <Tooltip title={paymentMethodTooltip}>
              <Stack as='span'>
                <InfoCircleIcon type='default' size={IconSize.xxs} color={theme.colors.gray300} />
              </Stack>
            </Tooltip>
          )}
        </Stack>
      );
    }

    let paymentAdditionalInfo: ReactNode = null;

    switch (paymentMethod.type) {
      case PaymentMethodType.CARD:
        if (!paymentMethod.details) {
          return 'Not available';
        }
        const { brand, last4, expMonth, expYear } = paymentMethod.details;
        paymentAdditionalInfo = (
          <Stack direction='vertical' gap={theme.spaces.x4}>
            <Stack direction='horizontal' gap={theme.spaces.x4}>
              <CreditCardBIcon type='default' />
              <Text level='sm' tone={theme.colors.gray600}>{`${brand} **** ${last4}`}</Text>
            </Stack>
            <Text level='sm' tone={theme.colors.gray600}>
              {`Expires on ${expMonth}/${expYear}`}
            </Text>
          </Stack>
        );
        break;
      case PaymentMethodType.CASH_APP:
        paymentAdditionalInfo = 'Cash App';
        break;
      case PaymentMethodType.AMAZON_PAY:
        paymentAdditionalInfo = 'Amazon Pay';
        break;
      default:
        paymentAdditionalInfo = 'Unknown Payment Method';
        break;
    }

    return paymentAdditionalInfo;
  };

  const canManagePaymentMethod = useMemo(() => {
    if (meteringInfo.userID !== user.id) {
      return false;
    }
    return true;
  }, [meteringInfo.userID, user.id]);

  const monthlyCreditsValue =
    error || serviceCreditsAllocated <= 0 ? (
      'Not available'
    ) : (
      <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
        <Text level='xl' weight={theme.fontWeight.semiBold} tone={theme.colors.gray700}>
          {roundToDecimals(serviceCreditsConsumed)} / {roundToDecimals(serviceCreditsAllocated)}
        </Text>
        <Text level='sm' tone={theme.colors.gray700}>
          {serviceUsagePercentage}%
        </Text>
      </Stack>
    );

  if (!isLoading && !currentWorkspace) {
    return <Navigate to={Private_Routes.Home.getUrl()} replace />;
  }

  // Show the payment method card if the user is not on a free plan and not automatically upgraded as payment method is not available for free plans / auto upgraded user.
  const isPlanFree = isFreePlan(activePlan?.name);
  const showPaymentMethodCard = !isPlanFree && !autoUpgraded;
  const roundedUserCreditsConsumed = roundToDecimals(userCreditsConsumed);
  return (
    <Stack direction='vertical' gap={theme.spaces.x20}>
      {activePlan && (
        <BillingOverViewContainer direction='horizontal' gap={theme.spaces.x16} align='space-between'>
          <BillingCard
            title='Current plan'
            value={activePlan?.displayName || 'Not set'}
            additionalInfo={computePlanRenewalInfo(activePlan, meteringInfo.to)}
            onClick={onPlanManage}
          />
          <BillingCard
            title='Monthly Credits Usage'
            value={monthlyCreditsValue}
            additionalInfo={error || isFreeTrial(activePlan?.name) ? '' : computeResetPeriod(meteringInfo.to)}
          />
          {!isPlanFree && (
            <BillingCard
              title='Workspace members'
              value={`${additionalUsers} ${pluralize(additionalUsers, 'user')}`}
              additionalInfo={`${roundedUserCreditsConsumed} ${pluralize(roundedUserCreditsConsumed, 'credit')}: $${activePlan?.price} ${formatPlanUnit(activePlan?.price, activePlan?.unit)}`}
            />
          )}
          <BillingCard
            title={customerBalance?.outstandingBalance ? 'Outstanding Balance' : 'Additional Usage'}
            value={computeAdditionalUsageAmount()}
            additionalInfo={
              isPlanFree ? (
                'Available on Pro and above'
              ) : (
                <Stack direction='vertical' gap={theme.spaces.x4}>
                  <Text level='xs' tone={theme.colors.gray500}>
                    Usage alert: {usageLimit.alertAmount ? `$${usageLimit.alertAmount}` : 'Not set'}
                  </Text>
                  <Text level='xs' tone={theme.colors.gray500}>
                    Budget alert: {usageLimit.budgetAmount ? `$${usageLimit.budgetAmount}` : 'Not set'}
                  </Text>
                </Stack>
              )
            }
            onClick={() => {
              track(EntityEvents.settings.billing.manageMonthlySpending, {
                teamId: currentWorkspaceId,
                trigger: 'billingOverview'
              });
              setShowManageMonthlySpendingDialog(true);
            }}
          />
          {showPaymentMethodCard && (
            <BillingCard
              title='Payment method'
              value={formatPaymentMethod()}
              buttonText={canManagePaymentMethod ? 'Manage' : undefined}
              onClick={handleManagePaymentMethod}
            />
          )}
        </BillingOverViewContainer>
      )}
      {showManageMonthlySpendingDialog && (
        <ManageMonthlySpendingDialog onClose={() => setShowManageMonthlySpendingDialog(false)} />
      )}
    </Stack>
  );
};

const BillingOverViewContainer = styled(Stack)`
  width: 100%;
  /* Shadows/shadow-xs */
  box-shadow: 0 1px 2px 0 ${theme.shadows.xs};
`;

const StyledInfoCircleIcon = styled(InfoCircleIcon)`
  cursor: pointer;
  position: relative;
  top: 5px; /* Adjust this value to match the text baseline */
`;
